
import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Editor } from "primereact/editor";
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import JoditEditor from 'jodit-react';
import config from "../../config";

function AddNewBlog() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [contentData, setContentData] = useState([]);
  const editorRef = useRef(null);

  useEffect(() => {
    if (selectedOption) {
      const newItem = {
        type: selectedOption,
        added: false,
      };
      switch (selectedOption) {
        case "imageText":
          newItem.contentImage = null;
          newItem.contentEditor = "";
          break;
        case "editorOnly":
          newItem.contentEditor = "";
          break;
        case "galleryImages":
          newItem.contentGalleryImages = null;
          break;
        case "bannerImage":
          newItem.contentBannerImage = null;
          newItem.bannerText = "";
          break;
        default:
          return;
      }
      setContentData((prevData) => [...prevData, newItem]);
    }
  }, [selectedOption]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    featuredImage: null,
  });

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files.length > 0) {
      const file = files[0];
      const fileSize = file.size / (1024 * 1024); // Convert to MB
      if (fileSize > 10) {
        alert('File size exceeds 10 MB limit.');
        return;
      }
      setFormData((prevData) => ({
        ...prevData,
        [name]: file,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleImageUpload = async (base64Image, name) => {
    const formData = new FormData();
    formData.append('imageBase64', base64Image);
    formData.append('name', name);

    try {
      const response = await axios.post(`${config.BASE_URL}/api/admin/upload-image`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data.imageUrl;
    } catch (error) {
      console.error('Image upload failed:', error);
      return null;
    }
  };

  const handleRepeaterInputChange = (index, e, isEditor = false) => {
    const updatedContent = [...contentData];

    if (isEditor) {
      // Handle JoditEditor input (e is the new content string)
      updatedContent[index] = {
        ...updatedContent[index],
        contentEditor: e,
      };
      setContentData(updatedContent);
    } else {
      // Handle standard input events
      const { name, value, files } = e.target;

      if (files && files.length > 0) {
        const filePromises = Array.from(files).map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = async () => {
              const base64File = reader.result;

              try {
                const imageName = await handleImageUpload(base64File, name);
                resolve(imageName);
              } catch (error) {
                reject(error);
              }
            };

            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });

        Promise.all(filePromises)
          .then((imageNames) => {
            updatedContent[index] = {
              ...updatedContent[index],
              [name]: imageNames,
            };
            setContentData(updatedContent);
          })
          .catch((error) => {
            console.error("Error uploading files:", error);
            alert("There was an error uploading the image(s).");
          });
      } else {
        updatedContent[index] = {
          ...updatedContent[index],
          [name]: value,
        };
        setContentData(updatedContent);
      }
    }
  };

  const handleEditorChange = (index, newValue) => {
    setContentData(prevContentData => {
      const updatedContent = [...prevContentData];
      updatedContent[index] = {
        ...updatedContent[index],
        contentEditor: newValue,
      };
      return updatedContent;
    });
  };

  const removeRepeaterItem = (index) => {
    setContentData((prevData) => prevData.filter((_, i) => i !== index));
  };

  const addRepeaterData = () => {
    if (selectedOption && contentData.length > 0) {
      const updatedContent = [...contentData];
      updatedContent[contentData.length - 1] = {
        ...updatedContent[contentData.length - 1],
        added: true,
      };
      setContentData(updatedContent);
      setSelectedOption("");
    }
  };

  const handleAddBlog = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('category', formData.category);
    data.append('contentData', JSON.stringify(contentData));

    if (formData.featuredImage) {
      data.append('featuredImage', formData.featuredImage);
    }

    try {
      const response = await axios.post(`${config.BASE_URL}/api/admin/add-blog`, data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        const token = localStorage.getItem('token');
        navigate(token ? '/admin/blogs' : '/admin/login');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add blog');
    }
  };

  const editorConfig = useMemo(() => ({
    height: 400,
    toolbarAdaptive: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    buttons: [
            'bold',
            'italic',
            'underline',
            'strikethrough',
            '|',
            'ul',
            'ol',
            '|',
            'center',
            'left',
            'right',
            'justify',
            '|',
            'link',
            'undo',
            'redo',
            '|',
            'source', '|',
            'outdent', 'indent', 'font', 'fontsize', 'brush', 'paragraph', '|',
        ],
    placeholder: 'Start typing your content here...',
  
    style: {
      color: '#000000',
      background: '#ffffff',
      fontSize: '16px',
    },
    limitChars: 10000,
    autofocus: true,
  }), []);

  return (
    <div className="dashboard">
      <div className="container">
        <h1 className="dash_title">Dashboard</h1>
        <div className="main-section">
          <div className="back_btn_wrap">
            <button className="back-btn" onClick={() => navigate(-1)}>Go Back</button>
          </div>
          <div className="main-inner add_project_main">
            <form onSubmit={handleAddBlog} encType="multipart/form-data">
              <div className="form-login">
                <div className="add_field titleField">
                  <h2>Blog Title</h2>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="add_field textareaField">
                  <h2>Description</h2>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="add_field textareaField">
                  <h2>Category</h2>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="add_field repeaterFieldsData titleField">
                  <h2>Blog Content</h2>
                  {contentData.length > 0 && contentData.map((item, index) => {
                    if (item.added === true) {
                      if (item.type === "imageText") {
                        return (
                          <>
                            <div className="singleReapterField" key={index}>
                              <div>
                                <h3>Image and Text</h3>
                                <p>Current Image: {item.contentImage && item.contentImage[0] && (
                                  <img src={config.BASE_URL + item.contentImage[0]} alt="Content" style={{ maxWidth: '200px' }} />
                                )}</p>
                                <input style={{marginTop:'10px'}}
                                  type="file"
                                  name="contentImage"
                                  onChange={(e) => handleRepeaterInputChange(index, e)}
                                />
                                <h4 style={{ marginTop: '25px' }}>Text</h4>
                              </div>
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                              <JoditEditor
                                ref={editorRef}
                                value={item.contentEditor}
                                config={editorConfig}
                                onChange={(newContent) => handleRepeaterInputChange(index, newContent, true)}
                              />
                            </div>
                            <span className="mt-10 mb-15 btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                          </>
                        );
                      }
                      if (item.type === "editorOnly") {
                        return (
                          <>
                            <div className="singleReapterField mt-15" key={index}>
                              <div>
                                <h3>Editor</h3>
                              </div>
                            </div>
                            <div style={{ marginBottom: '15px', border: '1px solid #ccc', minHeight: '400px', background: '#fff', color: '#000' }}>
                              <JoditEditor
                                ref={editorRef}
                                value={item.contentEditor}
                                config={editorConfig}
                                onChange={(newContent) => handleEditorChange(index, newContent)}
                              />
                            </div>
                            <span className="mt-10 mb-15 btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                          </>
                        );
                      }
                      if (item.type === "galleryImages") {
                        return (
                          <div className="singleReapterField mt-15" key={index}>
                            <div>
                              <h3>Gallery Images</h3>
                              <p>Current Images:</p>
                              {item.contentGalleryImages && item.contentGalleryImages.length > 0 && item.contentGalleryImages.map((img, imgIndex) => (
                                <div key={imgIndex}>
                                  <img src={config.BASE_URL + img} alt="Gallery" style={{ maxWidth: '200px', margin: '5px' }} />
                                </div>
                              ))}
                              <input  style={{marginTop:'10px'}}
                                type="file"
                                name="contentGalleryImages"
                                onChange={(e) => handleRepeaterInputChange(index, e)}
                                multiple
                              />
                              <span className="mt-10 mb-15 btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                            </div>
                          </div>
                        );
                      }
                      if (item.type === "bannerImage") {
                        return (
                          <div className="singleReapterField" key={index}>
                            <div>
                              <h3>Banner Image</h3>
                              <p>Current Image: {item.contentBannerImage && item.contentBannerImage[0] && (
                                <img src={config.BASE_URL + item.contentBannerImage[0]} alt="Banner" style={{ maxWidth: '200px' }} />
                              )}</p>
                              <input  style={{marginTop:'10px'}}
                                type="file"
                                name="contentBannerImage"
                                onChange={(e) => handleRepeaterInputChange(index, e)}
                              />
                              <h4 >Banner Text</h4>
                              <input
                                type="text"
                                name="bannerText"
                                value={item.bannerText}
                                onChange={(e) => handleRepeaterInputChange(index, e)}
                              />
                              <span className="mt-10 mb-15 btn removeItemBtn" onClick={() => removeRepeaterItem(index)}>Remove Item</span>
                            </div>
                          </div>
                        );
                      }
                    }
                    return null;
                  })}
                </div>

                <div className="add_field">
                  <h2>Select Option</h2>
                  <select
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                  >
                    <option value="">--Select an Option--</option>
                    <option value="imageText">Image + Text</option>
                    <option value="editorOnly">Editor Only</option>
                    <option value="galleryImages">Gallery Images</option>
                    <option value="bannerImage">Banner Image</option>
                  </select>
                </div>

                {selectedOption === "imageText" && contentData.length > 0 && (
                  <div className="add_field">
                    <h2>Image</h2>
                    <input
                      type="file"
                      name="contentImage"
                      onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                      required
                    />
                    <h2>Editor</h2>
                    <JoditEditor
                      ref={editorRef}
                      value={contentData[contentData.length - 1].contentEditor}
                      config={editorConfig}
                      onChange={(newContent) => handleRepeaterInputChange(contentData.length - 1, newContent, true)}
                    />
                  </div>
                )}

                {selectedOption === "editorOnly" && contentData.length > 0 && (
                  <div className="add_field">
                    <h2>Editor</h2>
                    <div style={{ border: '1px solid #ccc', minHeight: '400px', background: '#fff', color: '#000' }}>
                      <JoditEditor
                        ref={editorRef}
                        value={contentData[contentData.length - 1].contentEditor}
                        config={editorConfig}
                        onChange={(newContent) => handleEditorChange(contentData.length - 1, newContent)}
                      />
                    </div>
                  </div>
                )}

                {selectedOption === "galleryImages" && contentData.length > 0 && (
                  <div className="add_field">
                    <h2>Gallery Images</h2>
                    <input
                      type="file"
                      name="contentGalleryImages"
                      onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                      multiple
                      required
                    />
                  </div>
                )}

                {selectedOption === "bannerImage" && contentData.length > 0 && (
                  <div className="add_field">
                    <h2>Banner Image</h2>
                    <input
                      type="text"
                      name="bannerText"
                      value={contentData[contentData.length - 1].bannerText || ""}
                      onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                      required
                    />
                    <input style={{marginTop:'10px'}}
                      type="file"
                      name="contentBannerImage"
                      onChange={(e) => handleRepeaterInputChange(contentData.length - 1, e)}
                      required
                    />
                  </div>
                )}

                {selectedOption !== "" && (
                  <span onClick={addRepeaterData} style={{ background: "#fff", color: "#000", padding: "8px 30px", cursor: "pointer" }}>Add</span>
                )}

                <div className="add_field">
                  <h2>Featured Image</h2>
                  <div className="form-group">
                    <div className="file-upload">
                      <input
                        type="file"
                        className="form-control"
                        name="featuredImage"
                        onChange={handleInputChange}
                        required
                      />
                      <i className="fa fa-camera i-pic-upload"></i>
                    </div>
                  </div>
                </div>

                <div className="add_project">
                  <button type="submit">Add Blog</button>
                </div>
              </div>
            </form>
            {error && <p className="error-message">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddNewBlog;